import { useMemo, useRef, useState } from 'react';
import type { DashboardInputs } from './assembly';
import { text } from '../app/i18n';
import { formatDecimal } from './format';
import { SourceChart, SourceKpi, SourceTable, type SourceWidget } from './SourceWidgets';
import { useLoad } from './useLoad';
import { useAppServices } from './ViewContext';

/**
 * Tableau de bord (`view_kind: dashboard`, 59 §5.6, ADR-092 §5) : une grille de widgets. Un widget
 * KPI lit `GetKpiValues` — la définition du KPI est la seule source de la valeur (ADR-079 §9) ; un
 * widget alimenté par une requête nommée (`source`, S7f-d) lit cette requête, gouvernée par le serveur,
 * et se rend en carte, graphique ou tableau déclarés. Les sélecteurs de la page (liste d'objets
 * gouvernée) alimentent les paramètres « $selector.<id> ». Un échec ou une période vide restent dans
 * leur carte, jamais un écran d'erreur.
 */

/** Widget de `view-body#/properties/dashboard` (le type généré perd sa forme à cause du `allOf`). */
interface Widget extends SourceWidget {
  kpi_id?: string;
  period?: { grain: string; relative: string; count?: number };
  scenario?: string;
}

interface Selector {
  selector_id: string;
  label: Record<string, string>;
  object_type: string;
  filter?: Record<string, string>;
  label_field: string;
  empty_label: Record<string, string>;
}

/** Sélecteur de page : la liste gouvernée de son type ; « aucun » d'abord, puis les objets lisibles. */
function SelectorControl({ selector, value, onChange }: { selector: Selector; value: string; onChange: (value: string) => void }) {
  const services = useAppServices();
  const filter: Record<string, Record<string, string>> = {};
  for (const [field, expected] of Object.entries(selector.filter ?? {})) filter[field] = { eq: expected };
  const [loaded] = useLoad<Array<Record<string, unknown>>>(`selector:${selector.object_type}:${JSON.stringify(filter)}`, async () => (await services.client.listObjects(selector.object_type, { filter, limit: 50 })).data);
  const options = loaded.status === 'ready' ? loaded.value.filter((entry) => typeof entry['id'] === 'string').map((entry) => ({ id: entry['id'] as string, label: String(entry[selector.label_field] ?? entry['id']) })) : [];
  const label = text(selector.label, 'fr', selector.selector_id);
  return (
    <label className="ay-dashboard__selector">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} disabled={loaded.status !== 'ready'}>
        <option value="">{text(selector.empty_label, 'fr', 'Aucun')}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Paramètres d'une source : « $selector.<id> » prend la valeur choisie, absent si rien n'est choisi. */
function sourceParameters(widget: Widget, selected: Readonly<Record<string, string>>): Record<string, string> {
  const parameters: Record<string, string> = {};
  for (const [name, value] of Object.entries(widget.source?.parameters ?? {})) {
    if (value.startsWith('$selector.')) {
      const chosen = selected[value.slice('$selector.'.length)];
      if (chosen !== undefined && chosen !== '') parameters[name] = chosen;
    } else parameters[name] = value;
  }
  return parameters;
}

function SourceWidgetCard({ widget, selected, load }: { widget: Widget; selected: Readonly<Record<string, string>>; load: (name: string, parameters: Record<string, string>) => Promise<unknown> }) {
  const parameters = sourceParameters(widget, selected);
  const key = `source:${widget.source!.query_name}:${JSON.stringify(parameters)}`;
  const [loaded] = useLoad<unknown>(key, () => load(widget.source!.query_name, parameters));
  const title = text(widget.title, 'fr', widget.widget_id);
  const wide = widget.kind === 'chart' || widget.kind === 'table';
  let body;
  if (loaded.status === 'loading') body = <p className="ay-kpi__state">Chargement…</p>;
  else if (loaded.status === 'error') body = <p className="ay-kpi__state">Indisponible</p>;
  else if (loaded.value === null || typeof loaded.value !== 'object') body = <p className="ay-kpi__state">Aucune donnée</p>;
  else {
    const data = loaded.value as Record<string, unknown>;
    body = widget.kind === 'table' ? <SourceTable widget={widget} data={data} /> : widget.kind === 'chart' ? <SourceChart widget={widget} data={data} /> : <SourceKpi widget={widget} data={data} />;
  }
  return (
    <article className={`ay-kpi${wide ? ' ay-kpi--wide' : ''}`} aria-label={title}>
      <h2 className="ay-kpi__title">{title}</h2>
      {widget.description !== undefined && <p className="ay-kpi__description">{text(widget.description, 'fr', '')}</p>}
      {body}
    </article>
  );
}

interface KpiValueOut {
  value: number;
  /** Valeur exacte en chaîne décimale (LO4) : un montant se formate depuis elle, jamais depuis `value`. */
  exact_value?: string;
  unit?: string;
  dimensions?: Record<string, string>;
}

function formatValue(entry: KpiValueOut): string {
  const { value, unit } = entry;
  if (unit !== undefined && /^[A-Z]{3}$/.test(unit) && entry.exact_value !== undefined) return formatDecimal(entry.exact_value, 'fr', { style: 'currency', currency: unit });
  if (unit === '%' || unit === 'percent' || unit === 'ratio_pct') return `${value.toLocaleString('fr', { maximumFractionDigits: 1 })} %`;
  const formatted = value.toLocaleString('fr', { maximumFractionDigits: 2 });
  return unit === undefined || unit === '' ? formatted : `${formatted} ${unit}`;
}

function KpiWidget({ widget }: { widget: Widget }) {
  const services = useAppServices();
  const parameters: Record<string, string> = { kpi_id: widget.kpi_id ?? '', grain: widget.period?.grain ?? 'month' };
  if (widget.scenario !== undefined) parameters['scenario'] = widget.scenario;
  if (widget.period?.relative === 'previous') parameters['compare'] = 'previous';
  const [loaded] = useLoad<{ values: KpiValueOut[] }>(`kpi:${JSON.stringify(parameters)}`, async () => (await services.client.query<{ values: KpiValueOut[] }>('GetKpiValues', parameters)).data);
  const title = text(widget.title, 'fr', widget.kpi_id ?? widget.widget_id);
  let body;
  if (loaded.status === 'loading') body = <p className="ay-kpi__state">Chargement…</p>;
  else if (loaded.status === 'error') body = <p className="ay-kpi__state">Indisponible</p>;
  else {
    const values = Array.isArray(loaded.value.values) ? loaded.value.values.filter((entry) => typeof entry.value === 'number') : [];
    if (values.length === 0) body = <p className="ay-kpi__state">Aucune donnée</p>;
    else if (values.length === 1 && Object.keys(values[0]!.dimensions ?? {}).length === 0) body = <p className="ay-kpi__value">{formatValue(values[0]!)}</p>;
    else
      body = (
        <dl className="ay-kpi__list">
          {values.map((entry) => {
            const key = Object.values(entry.dimensions ?? {}).join(' · ') || 'Total';
            return (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{formatValue(entry)}</dd>
              </div>
            );
          })}
        </dl>
      );
  }
  return (
    <article className="ay-kpi" aria-label={title}>
      <h2 className="ay-kpi__title">{title}</h2>
      {body}
    </article>
  );
}

export function DashboardRepresentation({ dashboard }: { dashboard: DashboardInputs }) {
  const services = useAppServices();
  const widgets = (dashboard.widgets ?? []) as unknown as Widget[];
  const selectors = ((dashboard as { selectors?: unknown }).selectors ?? []) as Selector[];
  const [selected, setSelected] = useState<Record<string, string>>({});
  // Une même requête lue par plusieurs widgets (cartes, graphique, tableau) part une seule fois par jeu de paramètres.
  const cache = useRef(new Map<string, Promise<unknown>>());
  const load = useMemo(
    () => (name: string, parameters: Record<string, string>) => {
      const key = `${name}:${JSON.stringify(parameters)}`;
      let found = cache.current.get(key);
      if (found === undefined) {
        found = services.client.query<unknown>(name, parameters).then((envelope) => envelope.data);
        found.catch(() => cache.current.delete(key));
        cache.current.set(key, found);
      }
      return found;
    },
    [services],
  );
  if (widgets.length === 0) return <p className="ay-text ay-text--muted">Ce tableau de bord n’a aucun widget.</p>;
  return (
    <div className="ay-dashboard">
      {selectors.length > 0 && (
        <div className="ay-dashboard__selectors">
          {selectors.map((selector) => (
            <SelectorControl key={selector.selector_id} selector={selector} value={selected[selector.selector_id] ?? ''} onChange={(value) => setSelected((current) => ({ ...current, [selector.selector_id]: value }))} />
          ))}
        </div>
      )}
      {widgets.map((widget) =>
        widget.source !== undefined ? (
          <SourceWidgetCard key={widget.widget_id} widget={widget} selected={selected} load={load} />
        ) : widget.kind === 'kpi' && widget.kpi_id !== undefined ? (
          <KpiWidget key={widget.widget_id} widget={widget} />
        ) : (
          <article key={widget.widget_id} className="ay-kpi ay-kpi--pending" aria-label={text(widget.title, 'fr', widget.widget_id)}>
            <h2 className="ay-kpi__title">{text(widget.title, 'fr', widget.widget_id)}</h2>
            <p className="ay-kpi__state">Ce widget arrive avec les composants P1.</p>
          </article>
        ),
      )}
    </div>
  );
}
